<template>
  <TolModal
    :title="$t('projects.eventTypes.details.techRates.add')"
    :submit-button-text="$t('projects.details.labels.save')"
    @close="clearFormAndClose"
    @submit="save"
  >
    <template v-slot:body>
      <form>
        <div class="form-group">
          <div class="form-control">
            <label>
              {{ $t('projects.eventTypes.details.techRates.technicianLevel') }}
              <span class="asterisk">*</span>
            </label>
            <DropdownSelect
              :menu-items="rateTypes"
              :selected-item="null"
              :filter-on="['code', 'description']"
              class="bordered small"
              @select-item="techRate.rate_code = $event.code"
            >
              <template v-slot:selectedItem="{ value }">
                {{ value.code }} - {{ value.description }}
              </template>
              <template v-slot:dropdownItem="{ value }">
                {{ value.code }} - {{ value.description }}
              </template>
            </DropdownSelect>
          </div>
          <div class="form-row">
            <div class="form-control">
              <label>
                {{ $t('projects.eventTypes.details.techRates.payStaff') }}
                <span class="asterisk">*</span>
              </label>
              <InputField
                v-model="techRate.pay_staff"
                input-type="number"
                class="bordered"
                :class="{ error: formErrors['payStaff'] }"
                @input="techRate.pay_staff = $event"
              />
            </div>
            <div class="form-control">
              <label>
                {{ $t('projects.eventTypes.details.techRates.paySole') }}
                <span class="asterisk">*</span>
              </label>
              <InputField
                v-model="techRate.pay_sole"
                input-type="number"
                class="bordered"
                :class="{ error: formErrors['paySole'] }"
                @input="techRate.pay_sole = $event"
              />
            </div>
          </div>
          <div class="form-row">
            <div class="form-control">
              <label>
                {{ $t('projects.eventTypes.details.techRates.payInc') }}
                <span class="asterisk">*</span>
              </label>
              <InputField
                v-model="techRate.pay_inc"
                input-type="number"
                class="bordered"
                :class="{ error: formErrors['payInc'] }"
                @input="techRate.pay_inc = $event"
              />
            </div>
            <div class="form-control">
              <label>
                {{ $t('projects.eventTypes.details.techRates.billRate') }}
                <span class="asterisk">*</span>
              </label>
              <InputField
                v-model="techRate.bill_rate"
                input-type="number"
                class="bordered"
                :class="{ error: formErrors['billRate'] }"
                @input="techRate.bill_rate = $event"
              />
            </div>
          </div>
          <div class="form-row">
            <div class="form-control">
              <label>
                {{ $t('projects.eventTypes.details.techRates.startDate') }}
                <span class="asterisk">*</span>
              </label>
              <FormDatePicker
                v-model="formattedStartDate"
                type="bordered"
                @change="onSelectStartDate"
              />
            </div>
            <div class="form-control">
              <label>
                {{ $t('projects.eventTypes.details.techRates.endDate') }}
              </label>
              <FormDatePicker
                v-model="formattedEndDate"
                type="bordered"
                @change="onSelectEndDate"
              />
            </div>
          </div>
          <div class="form-control">
            <label>
              {{ $t('projects.eventTypes.details.techRates.comments') }}
            </label>
            <TolTextArea
              v-model="techRate.comments"
              class="form-textarea"
              @change="techRate.comments = $event.target.value"
            />
          </div>
        </div>
      </form>
    </template>
  </TolModal>
</template>

<script>
import format from 'date-fns/format';
import { AppGetters } from '@/app/app.keys';
import { dateUtils } from '@/mixins/date-utils';
import DropdownSelect from '@/app/components/DropdownSelect';
import FormDatePicker from '@/app/components/FormDatePicker';
import InputField from '@/app/components/InputField';
import TolModal from '@/app/components/TolModal';
import TolTextArea from '@/app/components/TolTextArea';

export default {
  name: 'AddTechRatesModal',
  components: {
    DropdownSelect,
    FormDatePicker,
    InputField,
    TolModal,
    TolTextArea
  },
  mixins: [dateUtils],
  data() {
    return {
      techRate: {
        rate_code: '',
        pay_staff: null,
        pay_sole: null,
        pay_inc: null,
        bill_rate: null,
        start_date: null,
        end_date: null,
        comments: ''
      }
    };
  },
  computed: {
    formattedStartDate() {
      return this.techRate.start_date ? this.dateFromString(this.techRate.start_date) : null;
    },
    formattedEndDate() {
      return this.techRate.end_date ? this.dateFromString(this.techRate.end_date) : null;
    },
    formErrors() {
      return this.$store.getters.ProjectEventTypeTechRatesValidationErrors;
    },
    rateTypes() {
      return this.$store.getters[AppGetters.AssignmentRateTypes];
    }
  },
  methods: {
    clearFormAndClose() {
      this.techRate = {
        rate_code: '',
        pay_staff: null,
        pay_sole: null,
        pay_inc: null,
        bill_rate: null,
        start_date: null,
        end_date: null,
        comments: ''
      };
      this.$emit('close');
    },
    onSelectStartDate(event) {
      this.techRate.start_date = format(event, 'YYYY-MM-DD');
    },
    onSelectEndDate(event) {
      this.techRate.end_date = format(event, 'YYYY-MM-DD');
    },
    async save() {
      const payload = {
        field: 'technician_rates',
        type: 'TECH_RATES',
        value: this.techRate
      };
      this.$emit('save', payload);
    }
  }
};
</script>

<style scoped lang="scss">
.form-group > div {
  margin-bottom: 1rem;
}

.form-row {
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  @media only screen and (min-width: 600px) {
    flex-direction: row;
  }

  .form-control {
    min-width: 45%;
  }
}

@media only screen and (max-width: 599px) {
  .form-row > div {
    margin-bottom: 1rem;
  }
}

.form-textarea {
  min-width: 30rem;
}

.asterisk {
  color: red;
}
</style>
